import { ProductWithdrawalMetricsData } from '@linka/domain/dtos';
import { eventDashboardQueries } from '@linka/http/event-dashboard';
import { useTranslation } from '@linka/i18n';
import { formatDate } from '@linka/utils';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useParams } from 'react-router';
import { ColumnDef, DataTable } from '../../../data-table/data-table';
import { Breadcrumb } from '../breadcrumb';
import { ExportChartTriggerButton } from '../components';
import { ExportFlowWrapper } from '../export-flow-wrapper';
import { DashboardFilters, DashboardFiltersDatePicker, dashboardFiltersAtom } from '../filters';
import { useSearchFilters } from '../hooks/use-search-filters';
import { useSystemFilters } from '../hooks/use-system-filters';

interface DetailedProductWithdrawalsProps {
  userEmail?: string;
}

export const DetailedProductWithdrawals = ({ userEmail }: DetailedProductWithdrawalsProps) => {
  const t = useTranslation();
  const { eventId = '' } = useParams();
  const { setFilter } = useSearchFilters();
  const systemFilters = useSystemFilters();
  const dashboardFilters = useAtomValue(dashboardFiltersAtom);
  const { data: withdrawals, isPending } = useQuery(
    eventDashboardQueries.productWithdrawals(eventId, {
      startDate: dashboardFilters.startDate,
      endDate: dashboardFilters.endDate,
      ...systemFilters,
    }),
  );

  const columns: ColumnDef<ProductWithdrawalMetricsData>[] = [
    {
      accessorKey: 'participantId',
      header: t('dashboard.detailed.participant-id'),
    },
    {
      accessorKey: 'participantName',
      header: t('dashboard.detailed.participant-name'),
    },
    {
      accessorKey: 'participantEmail',
      header: t('dashboard.detailed.participant-email'),
    },
    {
      accessorKey: 'participantPhone',
      header: t('dashboard.detailed.participant-phone'),
    },
    {
      accessorKey: 'productName',
      header: t('dashboard.detailed.item'),
    },
    {
      accessorKey: 'tagNumber',
      header: t('dashboard.detailed.tag-number'),
    },
    {
      accessorKey: 'withdrawalDate',
      header: t('dashboard.detailed.withdrawal-date'),
      cell: ({ row }) => (
        <span>{formatDate({ date: row.original.withdrawalDate, dateFormat: 'dd/MM/yyyy HH:mm' })}</span>
      ),
    },
    {
      accessorKey: 'withdrawalUserEmail',
      header: t('dashboard.detailed.withdrawal-operator-email'),
    },
  ];

  return (
    <div>
      <Breadcrumb hideHome />
      <header className='grid sm:grid-cols-[1fr,max-content] items-center gap-y-mini my-xxs'>
        <h1 className='ts-heading-sm sm:ts-heading-md text-ctx-content-title'>
          {t('dashboard.detailed.withdrawals-title')}
        </h1>
        <div className='ml-auto sm:ml-0 sm:mr-auto sm:col-start-1'>
          <DashboardFilters>
            <DashboardFiltersDatePicker />
          </DashboardFilters>
        </div>
        <ExportChartTriggerButton type='PRODUCT_WITHDRAWAL' className='col-span-2 self-end sm:col-span-1' />
      </header>
      <DataTable
        isPending={isPending}
        columns={columns}
        perPage={10}
        total={withdrawals?.total}
        data={withdrawals?.data || []}
        orderBy={systemFilters.orderBy}
        sortOrder={systemFilters.sortOrder}
        onSortChange={(col, dir) => setFilter({ orderBy: col, sortOrder: dir })}
        page={systemFilters.page}
        onPageChange={(page) => setFilter({ page: page.toString() })}
      />
      <ExportFlowWrapper userEmail={userEmail} />
    </div>
  );
};
